import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Info } from "lucide-react";

const AboutHero = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={ref}
      className="relative min-h-[calc(100vh-4rem)] lg:min-h-[80vh] bg-cover bg-center bg-no-repeat overflow-hidden flex items-center py-12 sm:py-16 pt-16 sm:pt-20"
      style={{
        backgroundImage: "url(/main.png)",
      }}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/50"></div>

      {/* Floating decorative elements */}
      <motion.div
        className="absolute right-10 top-1/4 w-40 h-40 rounded-full bg-gradient-to-br from-soft-saffron/20 to-copper-brown/20 blur-3xl hidden lg:block"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.7, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute left-10 bottom-1/4 w-32 h-32 rounded-full bg-gradient-to-tr from-forest-green/20 to-misty-sage/20 blur-2xl hidden lg:block"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.6, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Main Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center"
          >
            {/* Breadcrumb */}
            <motion.div variants={itemVariants} className="mb-4 sm:mb-6">
              <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-cloud-white/10 border border-cloud-white/20 backdrop-blur-sm">
                <Info className="w-3 h-3 sm:w-4 sm:h-4 text-soft-saffron" />
                <span className="text-cloud-white/80 text-xs sm:text-sm">
                  <a
                    href="/"
                    className="hover:text-soft-saffron transition-colors drop-shadow-sm"
                  >
                    Home
                  </a>
                  <span className="mx-2">•</span>
                  <span className="text-soft-saffron">About Us</span>
                </span>
              </div>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              variants={itemVariants}
              className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-cloud-white mb-4 sm:mb-6 drop-shadow-2xl"
            >
              About <span style={{ color: '#3FBA50' }}>Cittasya®</span>
            </motion.h1>

            {/* Subheading */}
            {/* <motion.h2
              variants={itemVariants}
              className="font-heading text-lg sm:text-xl md:text-2xl text-cloud-white/90 mb-4 sm:mb-6 drop-shadow-lg"
            >
              Ancient Wisdom for the Modern Soul
            </motion.h2> */}

            {/* Introductory Text */}
            <motion.p
              variants={itemVariants}
              className="text-base sm:text-lg md:text-xl text-cloud-white/90 leading-relaxed max-w-3xl mx-auto font-light drop-shadow-lg mb-6 sm:mb-8 px-4"
            >
              <p>Cittasya is a Sanskrit term pronounced as <em>chit-tas-ya.</em></p>&nbsp;It is related to <i>citta</i>, which represents human consciousness. Derived from root <em>cit</em> - meaning to perceive or think, it signifies how <i>citta shuddhi </i>(purifying the inner dimension) through Yoga Nidra can evolve the mind, renew perspective and transform life. 
            </motion.p>

            {/* Decorative divider */}
            <motion.div
              variants={itemVariants}
              className="flex items-center justify-center gap-2 sm:gap-4 mb-6 sm:mb-8"
            >
              <div className="w-12 sm:w-20 h-[2px] bg-gradient-to-r from-transparent to-soft-saffron"></div>
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-soft-saffron"></div>
              <div className="w-12 sm:w-20 h-[2px] bg-gradient-to-l from-transparent to-soft-saffron"></div>
            </motion.div>

            {/* Bottom CTA */}
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              transition={{ delay: 1 }}
              className="text-center px-4"
            >
              {/* <p className="text-cloud-white/70 text-sm sm:text-base max-w-2xl mx-auto drop-shadow-md mb-4">
                "The journey inward is the most profound adventure you'll ever
                take. Every step reveals new depths of peace and understanding."
              </p> */}

              {/* Animated dots */}
              {/* <div className="flex justify-center items-center gap-4">
                <motion.div
                  className="w-2 h-2 rounded-full bg-soft-saffron"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.div
                  className="w-3 h-3 rounded-full bg-cloud-white/60"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.8, 0.3] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                />
                <motion.div
                  className="w-2 h-2 rounded-full bg-copper-brown"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
              </div> */}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutHero;
